import routes from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { Identifier, TaskNames, TaskOptions, TaskResult } from './helper';

/** Response from the `invoke` endpoint */
interface InvokeResponse<K extends TaskNames> {
  identifier: Identifier;
  event?: TaskEvent<K>;
}

/** Response from the `events` endpoint when the task fails */
interface TaskFailureEvent {
  status: 'failure';
  error?: unknown;
}

/** Response from the `events` endpoint when the task succeeds */
interface TaskSuccessEvent<K extends TaskNames> {
  status: 'success';
  result: TaskResult<K>;
}

/** Response from the `events` endpoint */
type TaskEvent<K extends TaskNames> = TaskFailureEvent | TaskSuccessEvent<K>;

/**
 * Starts or monitors a server-side background task, returning a Promise to track status.
 * @param task_name Name of the task to execute.
 * @param identifier An identifier, used to resume/attach to a task possibly executed earlier.
 * @param options Options provided to the task
 */
export function invoke<K extends TaskNames>(
  task_name: K,
  identifier: Identifier,
  options: TaskOptions<K>,
): Promise<TaskResult<K>>;

/**
 * Starts a server-side background task and returns a Promise to track status.
 * Without an identifier, a new task will always be started.
 * @param task_name Name of the task to execute.
 * @param options Options provided to the task
 */
export function invoke<K extends TaskNames>(
  task_name: K,
  options: TaskOptions<K>,
): Promise<TaskResult<K>>;

export async function invoke<K extends TaskNames>(
  task_name: K,
  ...restArgs: [TaskOptions<K>] | [Identifier, TaskOptions<K>]
): Promise<TaskResult<K>> {
  let identifier: Identifier | undefined;
  let options: TaskOptions<K>;

  if (restArgs.length === 1) {
    identifier = undefined;
    [options] = restArgs;
  } else {
    [identifier, options] = restArgs;
  }

  const response = await fetchWithCsrf(
    routes.invoke_api_v0_usher_path({ subdomain_param: 'api', task_name }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        identifier,
        options,
      }),
    },
  );

  if (!response.ok) {
    if (response.status === 408) {
      return Promise.reject('TIMEOUT');
    } else {
      return Promise.reject('FETCH_ERROR');
    }
  }

  const json: InvokeResponse<K> = await response.json();

  // If the task was already finished, we don't have to wait and can just return now
  if (json.event) {
    switch (json.event.status) {
      case 'success':
        return Promise.resolve(json.event.result);
      case 'failure':
        return Promise.reject(json.event.error);
    }
  }

  // Identifier may have changed/been automatically assigned, and we'll need it for the next step
  identifier = json.identifier;

  const event_response = await fetch(
    routes.events_api_v0_usher_path({
      subdomain_param: 'api',
      task_name,
      identifier,
    }),
  );
  if (!event_response.ok) {
    if (event_response.status === 408) {
      return Promise.reject('TIMEOUT');
    } else {
      return Promise.reject('FETCH_ERROR');
    }
  }

  const event: TaskEvent<K> = await event_response.json();
  switch (event.status) {
    case 'success':
      return event.result;
    case 'failure':
      return Promise.reject(event.error);
  }
}

/**
 * Resolves the result of a server-started task via short non-blocking polls of
 * the invoke endpoint with `attach_only`, instead of the long-polling events
 * endpoint `invoke` uses. Each poll is a fast request that never parks a
 * server thread and never starts a job — the server owns starting the task;
 * this only reads. Rejects with TIMEOUT when the budget runs out.
 * @param task_name Name of the task to poll.
 * @param identifier The identifier the server started the task under.
 * @param intervalMs Delay between polls.
 * @param timeoutMs Total budget before rejecting with TIMEOUT.
 */
export async function pollTask<K extends TaskNames>(
  task_name: K,
  identifier: Identifier,
  { intervalMs = 2000, timeoutMs = 45000 } = {},
): Promise<TaskResult<K>> {
  const poll = async (): Promise<TaskResult<K> | null> => {
    const response = await fetchWithCsrf(
      routes.invoke_api_v0_usher_path({ subdomain_param: 'api', task_name }),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier, attach_only: true }),
      },
    );
    if (!response.ok) {
      throw new Error('FETCH_ERROR');
    }
    const json = (await response.json()) as InvokeResponse<K>;
    if (json.event == null) return null;
    if (json.event.status === 'success') return json.event.result;
    throw new Error(String(json.event.error ?? 'TASK_FAILED'));
  };

  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const result = await poll();
    if (result != null) return result;
    if (Date.now() >= deadline) throw new Error('TIMEOUT');
    await new Promise((resolve) => {
      setTimeout(resolve, intervalMs);
    });
  }
}

export const cancel = async <K extends TaskNames>(
  task_name: K,
  identifier: Identifier,
): Promise<void> => {
  const response = await fetchWithCsrf(
    routes.cancel_api_v0_usher_path({
      subdomain_param: 'api',
      task_name,
      identifier,
    }),
    { method: 'DELETE' },
  );
  if (!response.ok) throw Error('Error cancelling');
};
